import { useEffect, useState } from 'react'
import { Link, useParams, useNavigate } from 'react-router-dom'
import { supabase } from '../../lib/supabase'
import DashboardLayout from '../../components/dashboard/DashboardLayout'
import PageHeader from '../../components/dashboard/PageHeader'
import Card from '../../components/dashboard/Card'
import LoadingSpinner from '../../components/LoadingSpinner'

export default function MemberProfile() {
  const { id } = useParams()
  const navigate = useNavigate()
  const [member, setMember] = useState(null)
  const [groups, setGroups] = useState([])
  const [attendance, setAttendance] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    loadMemberProfile()
  }, [id])

  const loadMemberProfile = async () => {
    try {
      // Get member info with all fields
      const { data: memberData, error: memberError } = await supabase
        .from('users')
        .select('*')
        .eq('id', id)
        .single()

      if (memberError) throw memberError
      setMember(memberData)

      // Get group memberships (without foreign keys)
      const { data: groupMemberships } = await supabase
        .from('group_members')
        .select('group_id, user_id')
        .eq('user_id', id)

      // Fetch groups and user data separately
      if (groupMemberships && groupMemberships.length > 0) {
        const groupIds = groupMemberships.map(gm => gm.group_id)
        
        const { data: groupsData } = await supabase
          .from('groups')
          .select('id, name, depth')
          .in('id', groupIds)

        const { data: usersData } = await supabase
          .from('users')
          .select('id, leadership_level')
          .eq('id', id)
          .single()

        // Combine data
        const enrichedMemberships = groupMemberships.map(gm => ({
          groups: groupsData?.find(g => g.id === gm.group_id) || {},
          users: usersData || {}
        }))

        setGroups(enrichedMemberships)
      } else {
        setGroups([])
      }

      // Get attendance records (without foreign keys)
      const { data: attendanceRecords } = await supabase
        .from('attendance_records')
        .select('id, marked_at, event_id')
        .eq('user_id', id)
        .order('marked_at', { ascending: false })
        .limit(10)

      // Fetch events separately
      if (attendanceRecords && attendanceRecords.length > 0) {
        const eventIds = attendanceRecords.map(ar => ar.event_id).filter(Boolean)
        
        if (eventIds.length > 0) {
          const { data: eventsData } = await supabase
            .from('events')
            .select('id, title, start_date')
            .in('id', eventIds)

          // Combine data
          const enrichedAttendance = attendanceRecords.map(record => ({
            ...record,
            events: eventsData?.find(e => e.id === record.event_id) || {}
          }))

          setAttendance(enrichedAttendance)
        } else {
          setAttendance(attendanceRecords)
        }
      } else {
        setAttendance([])
      }

    } catch (error) {
      console.error('Load member profile error:', error)
    } finally {
      setLoading(false)
    }
  }

  if (loading) {
    return <LoadingSpinner message="Loading profile..." />
  }

  if (!member) {
    return (
      <DashboardLayout>
        <div className="p-6">
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-red-800">Member not found</p>
            <Link to="/admin/members" className="text-blue-600 hover:underline mt-2 inline-block">
              Back to Members
            </Link>
          </div>
        </div>
      </DashboardLayout>
    )
  }

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 'org_admin': return 'bg-purple-100 text-purple-800'
      case 'member': return 'bg-blue-100 text-blue-800'
      case 'unassigned': return 'bg-gray-100 text-gray-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  return (
    <DashboardLayout>
      <PageHeader
        title={member.name}
        description="Member profile"
      >
        <Link
          to="/admin/members"
          className="inline-flex items-center gap-2 rounded-lg border bg-white px-4 py-2 text-sm font-medium hover:bg-gray-50 transition-colors"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="19" y1="12" x2="5" y2="12"></line>
            <polyline points="12 19 5 12 12 5"></polyline>
          </svg>
          Back to Members
        </Link>
      </PageHeader>

      <div className="p-6">
        <div className="grid lg:grid-cols-2 gap-6">
          {/* Profile Information - Combined */}
          <Card title="Profile Information">
            <div className="space-y-6">
              {/* Avatar and Name */}
              <div className="flex items-center gap-4 pb-6 border-b">
                {member.profile_photo_url || member.avatar_url ? (
                  <img
                    src={member.profile_photo_url || member.avatar_url}
                    alt={member.name}
                    className="w-20 h-20 rounded-full object-cover border-2 border-gray-200"
                  />
                ) : (
                  <div className="w-20 h-20 rounded-full bg-blue-100 flex items-center justify-center border-2 border-blue-200">
                    <span className="text-2xl font-bold text-blue-600">
                      {member.name?.charAt(0) || 'M'}
                    </span>
                  </div>
                )}
                <div>
                  <h3 className="text-xl font-bold text-gray-900">{member.name}</h3>
                  <div className="flex gap-2 mt-2">
                    <span className={`px-3 py-1 text-xs rounded-full ${getRoleBadgeColor(member.role)}`}>
                      {member.role}
                    </span>
                    {member.leadership_level && (
                      <span className="px-3 py-1 text-xs rounded-full bg-yellow-100 text-yellow-800">
                        {member.leadership_level} leader
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* Contact Information */}
              <div className="space-y-4">
                <h4 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Contact Information</h4>
                
                <div>
                  <p className="text-sm text-gray-500 font-medium">Email Address</p>
                  <p className="text-gray-900 mt-1">{member.email || <span className="text-gray-400 italic">Not provided</span>}</p>
                </div>

                <div>
                  <p className="text-sm text-gray-500 font-medium">Phone Number</p>
                  <p className="text-gray-900 mt-1">{member.phone || <span className="text-gray-400 italic">Not provided</span>}</p>
                </div>

                <div>
                  <p className="text-sm text-gray-500 font-medium">Address</p>
                  <p className="text-gray-900 mt-1">{member.address || <span className="text-gray-400 italic">Not provided</span>}</p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-500 font-medium">City</p>
                    <p className="text-gray-900 mt-1">{member.city || <span className="text-gray-400 italic">Not provided</span>}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500 font-medium">State</p>
                    <p className="text-gray-900 mt-1">{member.state || <span className="text-gray-400 italic">Not provided</span>}</p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-500 font-medium">Country</p>
                    <p className="text-gray-900 mt-1">{member.country || <span className="text-gray-400 italic">Not provided</span>}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500 font-medium">Postal Code</p>
                    <p className="text-gray-900 mt-1">{member.postal_code || member.zip_code || <span className="text-gray-400 italic">Not provided</span>}</p>
                  </div>
                </div>
              </div>

              {/* Personal Information */}
              <div className="space-y-4 pt-4 border-t">
                <h4 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Personal Information</h4>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-500 font-medium">Date of Birth</p>
                    <p className="text-gray-900 mt-1">
                      {member.date_of_birth || member.dob ? 
                        new Date(member.date_of_birth || member.dob).toLocaleDateString('en-US', { 
                          year: 'numeric', 
                          month: 'long', 
                          day: 'numeric' 
                        }) : 
                        <span className="text-gray-400 italic">Not provided</span>
                      }
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500 font-medium">Gender</p>
                    <p className="text-gray-900 mt-1 capitalize">{member.gender || <span className="text-gray-400 italic">Not provided</span>}</p>
                  </div>
                </div>

                {(member.bio || member.notes) && (
                  <div>
                    <p className="text-sm text-gray-500 font-medium">Bio / Notes</p>
                    <p className="text-gray-900 mt-1 text-sm">{member.bio || member.notes}</p>
                  </div>
                )}
              </div>

              {/* Emergency Contact */}
              <div className="space-y-4 pt-4 border-t">
                <h4 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Emergency Contact</h4>
                
                <div>
                  <p className="text-sm text-gray-500 font-medium">Contact Name</p>
                  <p className="text-gray-900 mt-1">{member.emergency_contact_name || <span className="text-gray-400 italic">Not provided</span>}</p>
                </div>

                <div>
                  <p className="text-sm text-gray-500 font-medium">Contact Phone</p>
                  <p className="text-gray-900 mt-1">{member.emergency_contact_phone || <span className="text-gray-400 italic">Not provided</span>}</p>
                </div>

                {member.emergency_contact_relationship && (
                  <div>
                    <p className="text-sm text-gray-500 font-medium">Relationship</p>
                    <p className="text-gray-900 mt-1 capitalize">{member.emergency_contact_relationship}</p>
                  </div>
                )}
              </div>

              {/* Account Information */}
              <div className="space-y-4 pt-4 border-t">
                <h4 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Account Information</h4>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-500 font-medium">Status</p>
                    <span className={`inline-block mt-1 px-3 py-1 text-xs rounded-full ${
                      member.status === 'active' 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {member.status || 'Unknown'}
                    </span>
                  </div>

                  <div>
                    <p className="text-sm text-gray-500 font-medium">QR Code</p>
                    <div className="mt-1">
                      {member.qr_signature ? (
                        <span className="text-sm text-green-600 font-medium">✓ Generated</span>
                      ) : (
                        <span className="text-sm text-gray-400">Not Generated</span>
                      )}
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-500 font-medium">Member Since</p>
                    <p className="text-sm text-gray-900 mt-1">
                      {member.created_at ? new Date(member.created_at).toLocaleDateString('en-US', { 
                        year: 'numeric', 
                        month: 'long', 
                        day: 'numeric' 
                      }) : <span className="text-gray-400 italic">Unknown</span>}
                    </p>
                  </div>

                  <div>
                    <p className="text-sm text-gray-500 font-medium">Total Attendance</p>
                    <p className="text-sm font-bold text-blue-600 mt-1">{attendance.length} events</p>
                  </div>
                </div>

                {member.updated_at && (
                  <div>
                    <p className="text-sm text-gray-500 font-medium">Last Updated</p>
                    <p className="text-sm text-gray-900 mt-1">
                      {new Date(member.updated_at).toLocaleDateString('en-US', { 
                        year: 'numeric', 
                        month: 'long', 
                        day: 'numeric' 
                      })}
                    </p>
                  </div>
                )}
              </div>

              {/* Group Memberships */}
              <div className="pt-4 border-t">
                <h4 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">
                  Group Memberships ({groups.length})
                </h4>
                {groups.length === 0 ? (
                  <div className="text-center py-6 bg-gray-50 rounded-lg border-2 border-dashed border-gray-200">
                    <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="mx-auto text-gray-300 mb-2">
                      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                      <circle cx="9" cy="7" r="4"></circle>
                      <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                      <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                    </svg>
                    <p className="text-gray-500 text-sm">Not assigned to any groups</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {groups.map((membership, idx) => {
                      // Check if user is leader of this specific group
                      const leadershipMap = { team: 1, group: 2, subgroup: 3 }
                      const userLeadershipDepth = leadershipMap[membership.users?.leadership_level]
                      const isLeaderOfThisGroup = userLeadershipDepth === membership.groups.depth
                      
                      return (
                        <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                          <div className="flex items-center gap-3">
                            <div className={`w-2 h-2 rounded-full ${
                              membership.groups.depth === 1 ? 'bg-purple-500' :
                              membership.groups.depth === 2 ? 'bg-green-500' :
                              'bg-blue-500'
                            }`}></div>
                            <div>
                              <p className="font-medium text-gray-900 text-sm">{membership.groups.name}</p>
                              <p className="text-xs text-gray-500">
                                {membership.groups.depth === 1 ? 'Team' : 
                                 membership.groups.depth === 2 ? 'Group' : 
                                 'Subgroup'}
                              </p>
                            </div>
                          </div>
                          {isLeaderOfThisGroup && (
                            <span className="px-2 py-1 text-xs rounded-full bg-yellow-100 text-yellow-800 font-medium">
                              Leader
                            </span>
                          )}
                        </div>
                      )
                    })}
                  </div>
                )}
              </div>
            </div>
          </Card>

          {/* Recent Attendance */}
          <Card title="Recent Attendance" description="Last 10 events attended">
            {attendance.length === 0 ? (
              <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-200">
                <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="mx-auto text-gray-300 mb-3">
                  <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                  <line x1="16" y1="2" x2="16" y2="6"></line>
                  <line x1="8" y1="2" x2="8" y2="6"></line>
                  <line x1="3" y1="10" x2="21" y2="10"></line>
                </svg>
                <p className="text-gray-500 font-medium">No attendance records</p>
                <p className="text-gray-400 text-sm mt-1">This member hasn't attended any events yet</p>
              </div>
            ) : (
              <div className="space-y-3">
                {attendance.map((record) => (
                  <div key={record.id} className="flex items-start justify-between p-3 hover:bg-gray-50 rounded-lg transition-colors border-b last:border-0">
                    <div className="flex-1">
                      <p className="font-medium text-gray-900">{record.events?.title || 'Unknown Event'}</p>
                      <p className="text-sm text-gray-500 mt-1">
                        Event Date: {record.events?.start_date ? new Date(record.events.start_date).toLocaleDateString('en-US', { 
                          year: 'numeric', 
                          month: 'long', 
                          day: 'numeric' 
                        }) : 'Unknown'}
                      </p>
                    </div>
                    <div className="text-right ml-4">
                      <p className="text-xs text-gray-400">Attended</p>
                      <p className="text-xs text-gray-600 font-medium">
                        {new Date(record.marked_at).toLocaleDateString('en-US', { 
                          month: 'short', 
                          day: 'numeric' 
                        })}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </div>
      </div>
    </DashboardLayout>
  )
}